import * as React from 'react'
import Box from '@mui/material/Box'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepButton from '@mui/material/StepButton'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'

import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import SettingsFacebookConnect from '../../Settings/subScreens/SettingsFacebookConnect'
import { useSelector, Provider, useDispatch } from 'react-redux'
import {
  getConnectedFacebookPage,
  getUser,
  getUserPages,
  namedRequestData
} from '../../../store/common/common.selectors'
import { useCallback, useEffect, useState } from 'react'
import {
  NetworkType,
  SocialmediaConnection,
  SocialmediaConnectionState
} from '@professocialApi/professocial-core-frontend-api/models'
import SettingsInstagramConnect from '../../Settings/subScreens/SettingsInstagramConnect'
import { FacebookPage } from '@3m5/sm_publish-frontend-api'
import { getFacebookPage } from '../../../store/dashboard/dashboard.selector'
import { createStore, applyMiddleware } from 'redux'
import createSagaMiddleware from 'redux-saga'
import { composeWithDevTools } from '@redux-devtools/extension'
import { useTranslation } from 'react-i18next'

// FacebookSettings
import {
  facebookSettingsRootReducer
} from '@professocial/professocialFacebookSettings/src/store/facebookSettingsRoot.reducer'
import {
  facebookSettingsRootSagas
} from '@professocial/professocialFacebookSettings/src/store/facebookSettingsRoot.sagas'

import UserTopics from '../UserTopics/UserTopics'
import ImportProfessocialFacebookSettings
  from '../../../shared/components/ImportComponents/ImportProfessocialFacebookSettings'
import PreloaderButton from '@professocial/professocialFormComponents/FormComponents/PreloaderButton'
import { useHistory } from 'react-router-dom'
import { UpdateUserAccountRequest } from '@professocialApi/professocial-core-frontend-api/apis/UserLoginRESTServiceApi'
import { getCurrentUserInitiated, updateUserAccountInitiated } from '../../../store/common/common.actions'
import { CoreRootState } from '../../../store/coreRoot.reducer'
import { ActionCall } from '@professocial/professocialTypes/types/commonTypes'
import { UserSettings } from '@professocialApi/professocial-core-frontend-api'

// NetworkContent
const sagaNetworkContentMiddleware = createSagaMiddleware()
const storeFacebookSettings = createStore(
  facebookSettingsRootReducer,
  composeWithDevTools(
    applyMiddleware(sagaNetworkContentMiddleware),
  ),
)
sagaNetworkContentMiddleware.run(facebookSettingsRootSagas)

const useStyles = makeStyles((theme: Theme) => ({
  step: {
    '& .MuiStepIcon-text': {
      fill: theme.palette.common.white,
    },
  },
  callout: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    color: '#1A2027',
    border: 'solid 1px',
    borderRadius: '10px',
    backgroundColor: 'rgba(255, 249, 235, 0.6)',
    borderColor: '#FFDC48',
  },
}))

const UserWizardStepper: React.FC<{ setWizardCompleted: () => void }> = ({ setWizardCompleted }) => {
  const user: UserSettings | undefined = useSelector(getUser)
  const dispatch = useDispatch()
  const classes = useStyles()
  const { t } = useTranslation()
  const history = useHistory()

  const steps = [
    t('coreWizard:facebookPageSelection'),
    t('coreWizard:facebookPageInfo'),
    t('coreWizard:titleProfileImages'),
    t('coreWizard:instagramPageSelection'),
    t('coreWizard:topicsSelection')]

  const storedFacebookPage: FacebookPage | undefined = useSelector(getFacebookPage)

  const [activeStep, setActiveStep] = React.useState(0)
  const [completed, setCompleted] = React.useState<{ [k: number]: boolean }>({})
  const [disabled, setDisabled] = React.useState<{ [k: number]: boolean }>({})
  const [facebookConnection, setFacebookConnection] = useState<SocialmediaConnection>()
  const [connectedNetwork, setConnectedNetwork] = useState<boolean>(false)
  const [confirmed, setConfirmed] = useState<boolean>(false)
  const [savingFacebookPage, setSavingFacebookPage] = useState<boolean>(false)

  const dispatchUpdateUserAccount = useCallback((params: UpdateUserAccountRequest) => dispatch(updateUserAccountInitiated(params)), [dispatch])
  const dispatchGetUser = useCallback(() => dispatch(getCurrentUserInitiated()), [dispatch])

  const connectedUserPages = useSelector(getUserPages)
  const connectedFacebookPage: string | undefined = useSelector(getConnectedFacebookPage)
  const actionOnUpdateUserAccount: ActionCall | undefined = useSelector((state: CoreRootState) => namedRequestData(state, updateUserAccountInitiated.type))

  const completedSteps = () => {
    return Object.keys(completed).length
  }

  const totalSteps = () => {
    return steps.length
  }

  const isLastStep = () => {
    return activeStep === totalSteps() - 1
  }

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps()
  }

  const handleCompletedRequest = () => {
    setCompleted({ [activeStep]: true, ...completed })
    handleNext()
    setConfirmed(false)
  }

  const handleDisabledStep = () => {
    if (activeStep !== 0) return

    const updatedDisabledSteps = steps.reduce((disabledSteps, _, index) => {
      if (index !== 0) {
        disabledSteps[index] = !connectedNetwork
      }
      return disabledSteps
    }, { ...disabled })

    setDisabled(updatedDisabledSteps)
  }


  const handleComplete = () => {
    if (isLastStep()) {
      setCompleted({ 0: true, 1: true, 2: true, 3: true, 4: true })

      user?.id && dispatchUpdateUserAccount({
        userId: user.id,
        userLogin: { ...user, finishedWizard: true }
      })
    } else {
      if ([1, 2].includes(activeStep) && !completed[activeStep]) {
        return setConfirmed(true)
      }

      setCompleted(prev => ({ ...prev, [activeStep]: true }))
      handleNext()
    }
  }

  const handleStep = (step: number) => () => {
    setActiveStep(step)
  }

  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1
    setActiveStep(newActiveStep)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  useEffect(() => {
    if (allStepsCompleted()) return

    user?.smConnections?.forEach((connection) => {
      if (connection.networkType === NetworkType.Facebook) {
        setFacebookConnection(connection)
        if (connection.state === SocialmediaConnectionState.Connected && connectedFacebookPage) {
          setConnectedNetwork(true)
        }
      }
    })
  }, [user, connectedUserPages, connectedFacebookPage],
  )

  useEffect(() => {
    if (connectedNetwork) {
      setCompleted({ 0: true, ...completed })
      setActiveStep(1)
    }
  }, [connectedNetwork])

  useEffect(() => {
    const status = actionOnUpdateUserAccount?.status
    if (status && status === 'success') {
      dispatchGetUser()
      setCompleted({ [activeStep]: true, ...completed })
    }
  }, [actionOnUpdateUserAccount])

  useEffect(() => {
    handleDisabledStep()
  }, [connectedNetwork, activeStep])

  return (
    <Box sx={{ width: '100%' }}>
      {allStepsCompleted() ? (
        <Stack spacing={3} alignItems="center" width="100%">
          <Typography variant="h2">{t('coreWizard:congratulation')}</Typography>
          <Typography align="center">{t('coreWizard:wizardEnding')}</Typography>
          <ButtonComponent
            text={t('coreWizard:toDashboard')}
            onClick={() => {
              setWizardCompleted()
              history.replace('/user/dashboard/networkContent')
            }}
          />
        </Stack>
      ) : (
        <>
          <Typography variant="h2">{t('coreWizard:youAreWelcome')}</Typography>
          <Stepper sx={{ py: 5, px: 4 }} nonLinear activeStep={activeStep}>
            {steps.map((label, index) => (
              <Step key={label} completed={completed[index]} disabled={disabled[index]}>
                <StepButton color="inherit" className={classes.step} onClick={handleStep(index)}>
                  {label}
                </StepButton>
              </Step>
            ))}
          </Stepper>
          <Divider sx={{ mb: 4 }}/>
          {/* Content */}
          {activeStep === 0 && (
            <>
              <Box className={classes.callout}>
                <Typography variant="body2">{t('coreWizard:textFacebookConnection')}</Typography>
              </Box>
              <SettingsFacebookConnect connection={facebookConnection}/>
            </>
          )}
          {(activeStep === 1 || activeStep === 2) && (
            <Provider store={storeFacebookSettings}>
              {storedFacebookPage && (
                <ImportProfessocialFacebookSettings
                  config={storedFacebookPage}
                  activeStep={activeStep}
                  confirmed={confirmed}
                  handleCompleted={handleCompletedRequest}
                  setLoader={(value: boolean) => setSavingFacebookPage(value)}
                />
              )}
            </Provider>
          )}
          {activeStep === 3 && (
            <>
              <Box className={classes.callout}>
                <Typography variant="body2">{t('coreWizard:textInstagramConnection')}</Typography>
              </Box>
              <SettingsInstagramConnect connection={facebookConnection}/>
            </>
          )}
          {activeStep === 4 && (
            <>
              <Box className={classes.callout}>
                <Typography variant="body2">{t('coreWizard:textTopics')}</Typography>
              </Box>
              <UserTopics/>
            </>
          )}
          {/* End content */}
          <Divider sx={{ my: 4 }}/>
          <Stack direction="row" justifyContent="space-between">
            <Box>
              <ButtonComponent
                variant="outlined"
                color="secondary"
                disabled={activeStep === 0}
                onClick={handleBack}
                text={t('coreWizard:back')}
              />
            </Box>
            <Stack direction="row" spacing={2}>
              <Box>
                <PreloaderButton
                  position="right"
                  loading={savingFacebookPage}
                  disableButton={activeStep === 0 && !connectedNetwork}
                  onClick={handleComplete}
                  text={activeStep < steps.length - 1
                    ? t('coreWizard:continue')
                    : t('coreWizard:finish')}
                />
              </Box>
            </Stack>
          </Stack>
        </>
      )}
    </Box>
  )
}

export default UserWizardStepper
